import { isEmptyWhereClause } from '@modules/common/utils';
import { DashboardQuantifyChartBanner } from '@modules/dashboard/components/DashboardAnalysisContent/DashboardQuantifyChartBanner';
import { DashboardQuantifyChartFooter } from '@modules/dashboard/components/DashboardAnalysisContent/DashboardQuantifyChartFooter';
import {
  useFetchDashboardQuantifyReportData,
  useShouldShowDashboardQuantifyAsTable,
} from '@modules/dashboard/hooks';
import { useUpdateDashboardBarLabelWidthMetadata } from '@modules/dashboard/hooks/useUpdateDashboardBarLabelWidthMetadata';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { QuantifyChart } from '@modules/quantify/components/QuantifyChart';
import {
  useGetQuantifyCompareQuery,
  useSetAnalysisInQuantify,
  useUpdateQuantifyStoreFromURL,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Box, Skeleton } from '@mui/material';
import { AnalysisViewFragment } from 'generated/graphql';
import { useEffect } from 'react';
import { useMeasure } from 'react-use';
import { DashboardRecordsNotFound } from './DashboardRecordsNotFound';

interface DashboardQuantifyChartProps {
  analysis: AnalysisViewFragment;
  shouldDisableInteraction?: boolean;
}

export const DashboardQuantifyChart = ({
  analysis,
  shouldDisableInteraction,
}: DashboardQuantifyChartProps) => {
  const [bannerRef, { height: bannerHeight }] = useMeasure();

  const barLabelWidth = useDashboardStore(
    ({ metadata }) =>
      metadata.analysesMetadata.find((metadata) => metadata.id === analysis.id)
        ?.barLabelWidth,
  );
  const areDashboardGlobalFiltersPresent = useDashboardStore(({ query }) => {
    return !!query.duration || !isEmptyWhereClause(query.whereClause);
  });

  const isReportQueryInProgress = useQuantifyStore(
    ({ visual }) =>
      visual.isChartQueryInProgress || visual.isTableQueryInProgress,
  );
  const isReportQueryError = useQuantifyStore(
    ({ visual }) => visual.isTableQueryError || visual.isChartQueryError,
  );
  const chartDataPointsCount = useQuantifyStore(({ reportData, query }) =>
    !!query.compareQuery
      ? reportData.compare.chartDataPoints?.length ?? 0
      : reportData.quantify.chartDataPoints?.length ?? 0,
  );

  const shouldShowQuantifyTable = useShouldShowDashboardQuantifyAsTable(
    analysis.id,
  );

  const shouldFetchTableData = shouldShowQuantifyTable;
  const shouldFetchChartData = !shouldFetchTableData;

  const {
    isDataSetupComplete,
    isFetchingReportData: isFetchingInitialReportData,
  } = useUpdateQuantifyStoreFromURL({
    fetchPolicy: 'cache-and-network',
    shouldFetchChartData,
    shouldFetchTableData,
    shouldShowAllTableValues: false,
    shouldFetchDataAfterSetup: !areDashboardGlobalFiltersPresent, // If global filters are present, we fetch data in useFetchDashboardQuantifyReportData
  });

  useSetAnalysisInQuantify({
    analysis,
  });

  const compareQuery = useGetQuantifyCompareQuery();
  const chartDataPoints = useQuantifyStore(({ reportData }) =>
    compareQuery
      ? reportData.compare.chartDataPoints
      : reportData.quantify.chartDataPoints,
  );
  const tableRows = useQuantifyStore(({ reportData }) =>
    compareQuery ? reportData.compare.tableRows : reportData.quantify.tableRows,
  );

  const dispatch = useDashboardStore(({ dispatch }) => dispatch);

  const isDashboardQuantifyReportDataLoading =
    useFetchDashboardQuantifyReportData({
      skip: !isDataSetupComplete,
    });

  const isAnalysisLoading =
    isDashboardQuantifyReportDataLoading ||
    !isDataSetupComplete ||
    isReportQueryInProgress ||
    isFetchingInitialReportData;

  useEffect(() => {
    dispatch({
      type: DashboardActionType.SET_LOADING_ANALYSES_BY_ID,
      payload: {
        [analysis.id]: isAnalysisLoading,
      },
    });
  }, [analysis.id, dispatch, isAnalysisLoading]);

  const updateDashboardBarLabelWidthMetadata =
    useUpdateDashboardBarLabelWidthMetadata();

  const onBarChartResizeComplete = (labelWidthPercentage: number) => {
    updateDashboardBarLabelWidthMetadata(analysis.id, labelWidthPercentage);
  };

  if (isAnalysisLoading) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />;
  }

  // If there is error, we show the error message from chart & table components
  if (
    !isReportQueryError &&
    ((shouldFetchChartData && !chartDataPoints?.length) ||
      (shouldFetchTableData && !tableRows?.length))
  ) {
    return <DashboardRecordsNotFound />;
  }

  return (
    <Box height="100%">
      <Box ref={bannerRef}>
        <DashboardQuantifyChartBanner
          isTableView={shouldShowQuantifyTable}
          analysis={analysis}
        />
      </Box>
      <Box
        height={`calc(100% - ${bannerHeight}px)`}
        sx={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <QuantifyChart
          isQuantifyPageView={false}
          shouldShowToggleActions={false}
          shouldShowTitle={false}
          component={{
            trendChart: {
              containerSxProps: { p: 1 },
              shouldAutoAdjustHeight: true,
              shouldShowTimeRangeControls: true,
            },
            barChart: {
              onResizeComplete: onBarChartResizeComplete,
              defaultLabelWidth: barLabelWidth,
              sxProps: {
                px: 2,
              },
              shouldTakeFullHeight: true,
            },
            compareBarChart: {
              chartHeight: 350,
              barLabelSxProps: {
                fontSize: chartDataPointsCount > 10 ? '0.640rem' : '0.660rem',
              },
              onResizeComplete: onBarChartResizeComplete,
              defaultLabelWidth: barLabelWidth,
              sxProps: {
                px: 2,
              },
              shouldTakeFullHeight: true,
            },
          }}
          shouldDisableInteraction={shouldDisableInteraction}
          shouldShowTableView={shouldShowQuantifyTable}
          shouldShowMergeWithSelectedReasonsAction={false}
        />
      </Box>
      <DashboardQuantifyChartFooter isTableView={shouldShowQuantifyTable} />
    </Box>
  );
};
